class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.formId = this.getAttribute('form-id');
    this.originalFormAfterElement = null;
    this.originalForm = null;
    this.originalPriceElement = null;
    this.originalVariantRadios = null;
    this.originalAddToCartButton = null;
    this.originalProductInfo = null;
    this.stickyAddToCartButton = null;
    this.formBottomPosition = 0;
    this.customSelects = {};
    this.isVisibleByScroll = false;
    this.isVisibleByFocus = false;

    this.attachShadow({ mode: 'open' });

  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    this.originalFormAfterElement = document.getElementById('buy-buttons-after-element');
    this.originalForm = document.getElementById(this.formId);
    this.originalPriceElement = document.querySelector('.product__price');
    this.originalVariantRadios = document.querySelector('variant-radios');
    this.originalAddToCartButton = this.originalForm?.querySelector('button[type="submit"]');
    this.originalProductInfo = document.querySelector('product-info');
    this.stickyAddToCartButton = this.querySelector('#add-to-cart-button');

    this.stickyAddToCartButton.addEventListener('click', (e) => {
      const sizeCustomSelect = this.customSelects['size'];
      if (sizeCustomSelect && !sizeCustomSelect.selectedValue) {
        e.preventDefault();
        this.displayErrorMessage('Please select a size to add to your bag.');
      }
    });

    this.render();
    this.setupCustomSelects();
    this.setupEventListeners();
    this.updateButtonState();
    this.updatePrice();
    this.initializeSelectsFromOriginalForm();
    this.restoreFocusIfNeeded();


    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, this.handleCartErrors.bind(this));
  }

  render() {
    const template = this.querySelector('template');
    const clone = template.content.cloneNode(true);
    this.shadowRoot.appendChild(clone);
  }

  setupCustomSelects() {
    const selects = this.shadowRoot.querySelectorAll('custom-select');
    console.log('selects', selects);

    selects.forEach(select => {
      const optionType = select.getAttribute('option-type');
      this.customSelects[optionType] = select;

      select.addEventListener('select-change', this.handleSelectChange.bind(this));
    });
  }

  handleSelectChange(event) {
    const { optionType, value, label } = event.detail;

    if (optionType === 'color') {
      sessionStorage.setItem('sticky-atc-focus-restore', JSON.stringify({
        optionType,
        value,
        timestamp: Date.now(),
        wasVisible: this.classList.contains('visible')
      }));
    }

    this.selectOriginalRadio(optionType, value);

    this.clearErrorMessage();
  }

  selectOriginalRadio(optionType, value) {
    const otherFieldsets = this.originalVariantRadios?.querySelectorAll('fieldset') || [];
    const colorFieldset = this.originalProductInfo.querySelector('fieldset[data-product-option="color"]');

    const fieldsets = [...otherFieldsets, colorFieldset];

    let targetFieldset;

    for (const fieldset of fieldsets) {
      const dataOption = fieldset.dataset.productOption;

      if (dataOption === optionType) {
        targetFieldset = fieldset;
        break;
      }

      if (optionType === 'color') {
        this.customSelects['size']?.unselectAll();
      }
    }

    if (!targetFieldset) {
      console.error(`Could not find fieldset for option type: ${optionType}`);
      return;
    }
    const radio = targetFieldset.querySelector(`input[type="radio"][value="${value}"]`);

    if (!radio) {
      console.error(`Could not find radio input for ${optionType} with value: ${value}`);
      return;
    }

    if (radio.checked) return;
    radio.checked = true;
    this.triggerChangeEvent(radio);
  }

  initializeSelectsFromOriginalForm() {
    const otherFieldsets = this.originalVariantRadios?.querySelectorAll('fieldset') || [];
    const colorFieldset = this.originalProductInfo?.querySelector('fieldset[data-product-option="color"]');

    const fieldsets = [...otherFieldsets, colorFieldset];

    fieldsets.forEach(fieldset => {
      const optionType = fieldset.dataset.productOption;
      if (!optionType || !this.customSelects[optionType]) return;

      const radioInputs = fieldset.querySelectorAll('input[type="radio"]');
      const checkedRadio = Array.from(radioInputs).find(radio => radio.checked);

      if (checkedRadio) {
        const value = checkedRadio.value;
        this.customSelects[optionType].setSelection(value);
      }
    });
  }

  setupEventListeners() {
    const formRect = this.originalFormAfterElement.getBoundingClientRect();
    this.formBottomPosition = formRect.top + formRect.height + window.scrollY;

    this.setupVisibilityObserver();
    this.setupFormObservers();
    this.setupVariantRadioListeners();
    this.setupFocusVisibility();
  }

  setupVariantRadioListeners() {
    const otherFieldsets = this.originalVariantRadios?.querySelectorAll('fieldset') || [];
    const colorFieldset = this.originalProductInfo?.querySelector('fieldset[data-product-option="color"]');

    const fieldsets = [...otherFieldsets, colorFieldset];

    fieldsets.forEach(fieldset => {
      const optionType = fieldset.dataset.productOption;
      if (!optionType || !this.customSelects[optionType]) return;

      const radioInputs = fieldset.querySelectorAll('input[type="radio"]');
      radioInputs.forEach(radio => {
        radio.addEventListener('change', () => {
          if (radio.checked) {
            const value = radio.value;
            this.customSelects[optionType].setSelection(value);
          }
        });
      });
    });
  }

  setupVisibilityObserver() {
    this.observer = new IntersectionObserver((entries) => {
      const [entry] = entries;

      if (!entry.isIntersecting) {
        this.classList.add('visible');
        this.isVisibleByScroll = true;
      } else {
        this.classList.remove('visible');
        this.isVisibleByScroll = false;
      }
    });

    this.observer.observe(this.originalFormAfterElement);
  }

  setupFocusVisibility() {
    this.addEventListener('focusin', (event) => {
      const isInsideShadowRoot = event.composedPath().some(
        element => element === this.shadowRoot || element.getRootNode() === this.shadowRoot
      );

      if (isInsideShadowRoot || this.contains(event.target)) {
        if (!this.classList.contains('visible')) {
          this.classList.add('visible');
          this.isVisibleByFocus = true;
        }
      }
    });

    this.addEventListener('focusout', (event) => {
      const relatedTarget = event.relatedTarget;

      const isFocusStillInside = relatedTarget && (
        this.contains(relatedTarget) ||
        event.composedPath().some(element => element === this.shadowRoot)
      );

      if (!isFocusStillInside && this.isVisibleByFocus && !this.isVisibleByScroll) {
        this.classList.remove('visible');
        this.isVisibleByFocus = false;
      }
    });
  }


  setupFormObservers() {
    if (this.originalForm) {
      const observer = new MutationObserver(() => {
        this.updateButtonState();
        this.updateOptionsAvailability();
      });

      observer.observe(this.originalForm, { attributes: true, childList: true, subtree: true });
    }

    if (this.originalPriceElement) {
      const priceObserver = new MutationObserver(() => {
        this.updatePrice();
      });

      priceObserver.observe(this.originalPriceElement, { childList: true, subtree: true });
    }
  }

  updateButtonState() {
    if (this.originalAddToCartButton && this.stickyAddToCartButton) {
      const originalButtonText = this.originalAddToCartButton.querySelector('span')?.textContent || 'Add to Cart';
      this.stickyAddToCartButton.querySelector('span').textContent = originalButtonText;
      this.stickyAddToCartButton.disabled = this.originalAddToCartButton.disabled;

      // If the original button is disabled, and also "Sold Out", display "Notify Me" button
      // NOTE: Klaviyo Back-in-stock may need to be integrated here for the 2nd button
    }
  }

  updatePrice() {
    const priceElements = this.shadowRoot.querySelectorAll('[data-product-price]');

    if (priceElements.length && this.originalPriceElement) {
      priceElements.forEach(priceElement => {
        priceElement.innerHTML = '';
        priceElement.appendChild(this.originalPriceElement.cloneNode(true));
      });
    }
  }

  updateOptionsAvailability() {
    const otherFieldsets = this.originalVariantRadios?.querySelectorAll('fieldset') || [];
    const colorFieldset = this.originalProductInfo?.querySelector('fieldset[data-product-option="color"]');

    const fieldsets = [...otherFieldsets, colorFieldset];

    fieldsets.forEach(fieldset => {
      const optionType = fieldset.dataset.productOption;
      if (!optionType || !this.customSelects[optionType]) return;

      const radioInputs = fieldset.querySelectorAll('input[type="radio"]');
      radioInputs.forEach(radio => {
        const isDisabled = radio.classList.contains('disabled');
        this.customSelects[optionType].setDisability(radio.value, isDisabled);
      });
    });
  }

  triggerChangeEvent(element) {
    const event = new Event('change', { bubbles: true });
    element.dispatchEvent(event);
  }

  displayErrorMessage(message) {
    const errorContainers = this.shadowRoot.querySelectorAll('[data-warning-container]');
    if (errorContainers.length) {
      errorContainers.forEach(container => {
        container.innerHTML = message;
      });
    }
  }

  clearErrorMessage() {
    const errorContainers = this.shadowRoot.querySelectorAll('[data-warning-container]');
    if (errorContainers.length) {
      errorContainers.forEach(container => {
        container.innerHTML = '';
      });
    }
  }

  handleCartErrors(error) {
    if (error.message) {
      this.displayErrorMessage(error.message);
    }
  }

  restoreFocusIfNeeded() {
    try {
      const focusData = sessionStorage.getItem('sticky-atc-focus-restore');
      if (!focusData) return;

      const { optionType, value, timestamp, wasVisible } = JSON.parse(focusData);

      if (Date.now() - timestamp > 5000) {
        sessionStorage.removeItem('sticky-atc-focus-restore');
        return;
      }

      if (wasVisible && !this.classList.contains('visible')) {
        this.classList.add('visible');
      }

      const customSelect = this.customSelects[optionType];
      if (!customSelect) return;

      requestAnimationFrame(() => {
        const trigger = customSelect.shadowRoot?.querySelector('.select-trigger');
        if (trigger) {
          trigger.focus();

          const selectedOption = customSelect.getOptionObject(value);
          if (selectedOption) {
            const announcement = `${customSelect.label}: ${selectedOption.label} selected`;
            this.announceToScreenReader(announcement);
          }
        }

        sessionStorage.removeItem('sticky-atc-focus-restore');
      });
    } catch (error) {
      console.error('[StickyAddToCart] Error restoring focus:', error);
      sessionStorage.removeItem('sticky-atc-focus-restore');
    }
  }

  announceToScreenReader(message) {
    let announcer = document.getElementById('sticky-atc-announcer');
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.id = 'sticky-atc-announcer';
      announcer.setAttribute('role', 'status');
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.className = 'visually-hidden';
      document.body.appendChild(announcer);
    }
    announcer.textContent = '';
    requestAnimationFrame(() => {
      announcer.textContent = message;
    });
  }
}

customElements.whenDefined('custom-select').then(() => {
  // The StickyAddToCart heavly relies on the custom-select component, so we need to wait for it to be defined before defining the StickyAddToCart component.
  customElements.define('sticky-add-to-cart', StickyAddToCart);
});
